<script setup lang="ts">
/**
 * The card for a payment made with the family present, outside checkout: a
 * saved one, or a new one, saved first and then paid with.
 *
 * A card other than the one lessons are charged to pays for this alone unless
 * they tick the box. Checkout is the one place that decides it for them.
 */
import { computed, onMounted, ref } from 'vue'
import CardChooser from '@/Components/Payment/CardChooser.vue'
import PaymentElementForm from '@/Components/Payment/PaymentElementForm.vue'
import { useCardSetup } from '@/composables/useCardSetup'
import { listCards, NEW_CARD, type SavedCard } from '@/lib/savedCards'

const props = defineProps<{
  stripeKey: string
}>()

const cards = ref<SavedCard[]>([])
const chosen = ref<string | null>(null)
const isLoading = ref(true)
const loadError = ref('')
const newCardComplete = ref(false)
const remember = ref(false)
const setup = useCardSetup()

const isNewCard = computed(() => chosen.value === NEW_CARD)

const isReady = computed(() => (isNewCard.value ? newCardComplete.value : chosen.value !== null))

const offersToRemember = computed(
  () => chosen.value !== null && !cards.value.find(card => card.id === chosen.value)?.is_default
)

onMounted(async () => {
  try {
    cards.value = await listCards()
    await pick(cards.value.length === 0 ? NEW_CARD : (cards.value.find(card => card.is_default)?.id ?? null))
  } catch {
    loadError.value = 'We could not load your cards. Please refresh the page.'
  } finally {
    isLoading.value = false
  }
})

/** A new card is most often the answer to one that stopped working, so it is offered for lessons too. */
async function pick(card: string | null) {
  chosen.value = card
  remember.value = card === NEW_CARD

  if (card === NEW_CARD && !setup.setupSecret.value) {
    newCardComplete.value = false
    await setup.open()
  }
}

/**
 * The card to pay with. A new card is saved first, and then chosen like any
 * other, so trying again after a refusal does not save it twice.
 */
async function choose(): Promise<{ paymentMethodId: string; remember: boolean }> {
  if (!isNewCard.value) {
    return { paymentMethodId: chosen.value!, remember: offersToRemember.value && remember.value }
  }

  const paymentMethodId = await setup.save()

  cards.value = await listCards()
  chosen.value = paymentMethodId
  setup.close()

  return { paymentMethodId, remember: remember.value }
}

defineExpose({ choose, isReady })
</script>

<template>
  <div class="space-y-3">
    <p v-if="isLoading" class="text-sm text-gray-500">Loading your cards…</p>
    <p v-else-if="loadError" role="alert" class="text-sm text-red-600">{{ loadError }}</p>

    <CardChooser v-else :model-value="chosen" :cards="cards" @update:model-value="pick">
      <PaymentElementForm
        v-if="setup.setupSecret.value"
        :options="{ clientSecret: setup.setupSecret.value }"
        :stripe-key="props.stripeKey"
        @change="complete => (newCardComplete = complete)"
        @ready="setup.onReady"
      />
    </CardChooser>

    <label v-if="offersToRemember" class="flex items-start gap-2 text-sm text-gray-700">
      <input v-model="remember" type="checkbox" class="mt-0.5 rounded border-gray-300" />
      Charge my future lessons to this card
    </label>
  </div>
</template>
